/**
 * The Cabinet page's data, kept live without ever blanking the screen.
 *
 * Three behaviours, each fixing something the manual-load version got wrong:
 *
 * 1. STALE-WHILE-REVALIDATE — a refetch never clears what is already rendered.
 *    The full-page skeleton is for the FIRST load only, when there is genuinely
 *    nothing to show; after that a reload swaps the data underneath a live
 *    graph, so the user never watches their view disappear and come back.
 * 2. POLLING, gated on visibility — ingest happens in the background (an upload
 *    catalogs a project seconds later), so a page that only loads once is
 *    usually wrong. It polls while visible and STOPS while hidden: a background
 *    tab that keeps hitting the API is a cost with no reader.
 * 3. FAILURES DO NOT DESTROY DATA — a poll that fails leaves the last good
 *    catalog on screen and raises a stale flag. Blanking a working page because
 *    one background request timed out is strictly worse than showing slightly
 *    old truth and saying so.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cabinetService } from "../../services/cabinetService";
import type { CabinetGraph, CabinetSummary } from "../../types/cabinet";

export const CABINET_POLL_INTERVAL_MS = 10_000;

export interface CabinetData {
	graph: CabinetGraph | null;
	summary: CabinetSummary | null;
	/** True only before the first successful load — drives the skeleton. */
	loading: boolean;
	/** A load failed AND there is nothing on screen: the page is unusable. */
	error: Error | undefined;
	/** A refetch is in flight. Data stays rendered; this is for a subtle dot. */
	refreshing: boolean;
	/** The last attempt failed but we are still showing earlier data. */
	stale: boolean;
	/** Epoch ms of the last SUCCESSFUL load, for "updated Xs ago". */
	lastUpdatedAt: number | null;
	refresh: () => void;
}

/**
 * A cheap content signature. Not a hash — `JSON.stringify` of a payload this
 * size is well under a millisecond, and the alternative (a deep-compare
 * dependency, or a real digest) buys nothing at ten-second intervals.
 *
 * Its only job is to answer "did anything actually change?", so that an
 * unchanged poll keeps the previous OBJECT IDENTITY and every downstream memo —
 * including the chart's `setOption` — sees nothing to do.
 */
export function signature(value: unknown): string {
	return JSON.stringify(value ?? null);
}

export function useCabinetData(
	pollIntervalMs: number = CABINET_POLL_INTERVAL_MS,
): CabinetData {
	const [graph, setGraph] = useState<CabinetGraph | null>(null);
	const [summary, setSummary] = useState<CabinetSummary | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);

	// Signatures of what is currently on screen, so an unchanged poll can be a
	// no-op rather than a re-render with a new object of identical content.
	const graphSignature = useRef<string | null>(null);
	const summarySignature = useRef<string | null>(null);
	// Guards a state update after unmount, and a second poll overlapping a slow
	// one — the API is idempotent but two in flight would fight over `stale`.
	const mounted = useRef(true);
	const inFlight = useRef(false);

	const load = useCallback(async () => {
		if (inFlight.current) return;
		inFlight.current = true;
		setRefreshing(true);
		try {
			const [nextGraph, nextSummary] = await Promise.all([
				cabinetService.getCabinetGraph(),
				cabinetService.getCabinetSummary(),
			]);
			if (!mounted.current) return;

			if (nextGraph === null) {
				// The service degrades every failure to null. With data already on
				// screen this is a stale marker, not an error state — the catalog we
				// are showing was true a moment ago and still is, probably.
				setStale(true);
				setError((current) =>
					graphSignature.current === null
						? new Error("Could not load the cabinet catalog.")
						: current,
				);
				return;
			}

			const nextGraphSignature = signature(nextGraph);
			if (nextGraphSignature !== graphSignature.current) {
				graphSignature.current = nextGraphSignature;
				setGraph(nextGraph);
			}
			// The summary is not fatal: a missing one drops the strip, and the
			// graph is the page.
			const nextSummarySignature = signature(nextSummary);
			if (nextSummarySignature !== summarySignature.current) {
				summarySignature.current = nextSummarySignature;
				setSummary(nextSummary);
			}
			setError(undefined);
			setStale(false);
			setLastUpdatedAt(Date.now());
		} finally {
			if (mounted.current) {
				setRefreshing(false);
				setLoading(false);
			}
			inFlight.current = false;
		}
	}, []);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		if (pollIntervalMs <= 0) return;

		const isVisible = () =>
			typeof document === "undefined" || document.visibilityState !== "hidden";

		let timer: ReturnType<typeof setInterval> | null = null;
		const start = () => {
			if (timer !== null) return;
			timer = setInterval(() => {
				if (isVisible()) void load();
			}, pollIntervalMs);
		};
		const stop = () => {
			if (timer === null) return;
			clearInterval(timer);
			timer = null;
		};

		const onVisibilityChange = () => {
			if (isVisible()) {
				// Coming back is the moment the user most wants current data —
				// waiting out the rest of an interval would show them the catalog
				// as it was when they left.
				void load();
				start();
			} else {
				stop();
			}
		};

		if (isVisible()) start();
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			stop();
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [load, pollIntervalMs]);

	return useMemo(
		() => ({
			graph,
			summary,
			loading,
			error,
			refreshing,
			stale,
			lastUpdatedAt,
			refresh: () => void load(),
		}),
		[graph, summary, loading, error, refreshing, stale, lastUpdatedAt, load],
	);
}
